import React, { useState, useEffect } from 'react';
import { correspondenceAPI, adminAPI } from '../services/authService';
import { getChainAttachments, printCorrespondenceThread } from '../utils/correspondencePrint';
import { formatDateIN } from '../utils/dateFormat';
import ProjectNameText from './ProjectNameText';
import { viewAttachmentInNewTab, downloadAttachment } from '../utils/attachmentStorage';

const buildAttachmentFallback = (file, chainSubject) => [
  'Correspondence attachment',
  '-------------------------',
  `Subject: ${chainSubject || '-'}`,
  `File: ${file?.name || '-'}`,
  '',
  'If preview/download failed, open the correspondence subject and use View on the letter entry.',
].join('\n');

/**
 * @param {number|null|undefined} linkedCorrespondenceId — chain id
 * @param {function(number, { entryId?: number }|undefined): void} [onViewThread] — opens correspondence thread; pass entryId to scroll to that letter
 */
const LinkedCorrespondenceCard = ({ linkedCorrespondenceId, onViewThread }) => {
  const [showAttachments, setShowAttachments] = useState(false);
  const [linkedChain, setLinkedChain] = useState(null);
  const [entries, setEntries] = useState([]);
  const [project, setProject] = useState(null);

  useEffect(() => {
    if (!linkedCorrespondenceId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [chainResp, entriesResp] = await Promise.all([
          correspondenceAPI.getChainById(linkedCorrespondenceId),
          correspondenceAPI.getEntries(linkedCorrespondenceId),
        ]);
        if (cancelled) return;
        const chain = chainResp?.data || null;
        const entryList = entriesResp?.data || [];
        setLinkedChain(chain);
        setEntries(entryList);
        if (chain?.project_id) {
          const projResp = await adminAPI.getProjects();
          const body = projResp?.data;
          const rows = Array.isArray(body) ? body : (body?.data ?? []);
          const p = rows.find(r => r && r.id === chain.project_id);
          if (!cancelled) setProject(p ? { id: p.id, name: p.name, agreement_prefix: p.agreementPrefix || p.code || `PRJ-${p.id}` } : null);
        }
      } catch (_) {
        if (!cancelled) setLinkedChain(null);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [linkedCorrespondenceId]);

  const attachmentList = linkedChain ? getChainAttachments(linkedChain.id, entries) : [];

  const openThread = (entryId) => {
    if (!linkedCorrespondenceId || typeof onViewThread !== 'function') return;
    setShowAttachments(false);
    onViewThread(Number(linkedCorrespondenceId), entryId != null ? { entryId: Number(entryId) } : undefined);
  };

  return (
    <>
      <div style={{ marginBottom: 16, padding: 10, border: '1px solid #dbeafe', borderRadius: 6, background: '#F5F3FF' }}>
        <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>Linked Correspondence</p>
        <p style={{ fontSize: 13, color: '#1e3a8a', fontWeight: 500, margin: 0 }}>
          {linkedChain ? linkedChain.subject : 'No linked Correspondence Subject.'}
        </p>
        {linkedChain && project?.name && (
          <p style={{ margin: '8px 0 0', fontSize: 12 }}>
            <ProjectNameText as="span">{project.name}</ProjectNameText>
          </p>
        )}
        {linkedChain && (
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>
              Attachments: {attachmentList.length}
              {attachmentList.length > 0 ? ` (${attachmentList.slice(0, 3).map(a => a.name).join(', ')}${attachmentList.length > 3 ? ', ...' : ''})` : ''}
            </p>
            {typeof onViewThread === 'function' && (
              <button
                type="button"
                className="btn-primary btn-sm"
                onClick={() => openThread()}
              >
                Open correspondence subject
              </button>
            )}
            {attachmentList.length > 0 && (
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => setShowAttachments(true)}
              >
                View attachments
              </button>
            )}
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => printCorrespondenceThread({ chain: linkedChain, project, entries })}
            >
              Print full correspondence
            </button>
          </div>
        )}
      </div>

      {showAttachments && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 960 }}>
            <div className="modal-header">
              <h2>Correspondence attachments</h2>
              <button type="button" className="btn-close" onClick={() => setShowAttachments(false)}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              {typeof onViewThread === 'function' && linkedChain && (
                <p style={{ fontSize: 13, color: '#374151', marginTop: 0, marginBottom: 12 }}>
                  <button type="button" className="btn-primary btn-sm" onClick={() => openThread()}>
                    Open full correspondence subject
                  </button>
                  <span style={{ marginLeft: 10, fontSize: 12, color: '#6b7280' }}>
                    Opens the letter thread (same as Correspondence Register). Use <strong>Open letter</strong> on a row to jump to that entry.
                  </span>
                </p>
              )}
              {attachmentList.length === 0 ? (
                <p style={{ fontSize: 13, color: '#6b7280' }}>No attachments found.</p>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>File name</th>
                        <th>Entry ref</th>
                        <th>Date</th>
                        <th>Direction</th>
                        <th style={{ minWidth: 220 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attachmentList.map((file, index) => (
                        <tr key={`${file.entry_id}-${file.name}-${index}`}>
                          <td style={{ wordBreak: 'break-word', maxWidth: 280 }}>{file.name}</td>
                          <td>
                            {typeof onViewThread === 'function' && file.entry_id != null ? (
                              <button
                                type="button"
                                className="btn-link"
                                style={{ color: '#7C3AED', fontWeight: 600, padding: 0, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => openThread(file.entry_id)}
                              >
                                {file.ref_no}
                              </button>
                            ) : (
                              <span style={{ color: '#7C3AED', fontWeight: 500 }}>{file.ref_no}</span>
                            )}
                          </td>
                          <td>{formatDateIN(file.letter_date)}</td>
                          <td>{file.direction}</td>
                          <td>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              <button
                                type="button"
                                className="btn-secondary btn-sm"
                                onClick={() => viewAttachmentInNewTab(file, () => downloadAttachment(file, buildAttachmentFallback(file, linkedChain?.subject)))}
                              >
                                View file
                              </button>
                              <button
                                type="button"
                                className="btn-secondary btn-sm"
                                onClick={() => downloadAttachment(file, buildAttachmentFallback(file, linkedChain?.subject))}
                              >
                                Download
                              </button>
                              {typeof onViewThread === 'function' && file.entry_id != null && (
                                <button
                                  type="button"
                                  className="btn-secondary btn-sm"
                                  onClick={() => openThread(file.entry_id)}
                                >
                                  Open letter
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LinkedCorrespondenceCard;
